import { useCallback, useEffect, useRef, useState } from "react";
import type { PipCorner } from "../store/streamPipStore";

export interface PipDrag {
  offset: { x: number; y: number };
  dragging: boolean;
  onPointerDown(event: React.PointerEvent): void;
}

const ORIGIN = { x: 0, y: 0 };

function cornerFor(x: number, y: number): PipCorner {
  const horizontal = x < window.innerWidth / 2 ? "l" : "r";
  const vertical = y < window.innerHeight / 2 ? "t" : "b";
  return `${vertical}${horizontal}` as PipCorner;
}

/**
 * Pointer drag for the mini player. Movement is tracked on `window` rather than
 * with pointer capture so the gesture survives the pointer leaving the card —
 * and so it is testable in jsdom, which has no pointer capture.
 */
export function usePipDrag({
  enabled,
  onSnap,
}: {
  enabled: boolean;
  onSnap(corner: PipCorner): void;
}): PipDrag {
  const [offset, setOffset] = useState(ORIGIN);
  const [dragging, setDragging] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => cleanupRef.current?.(), []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      // A second pointerdown mid-gesture would attach a second listener pair
      // and overwrite cleanupRef, orphaning the first pair on window forever.
      // An active gesture is precisely a non-null cleanupRef.
      if (!enabled || cleanupRef.current) return;
      const startX = event.clientX;
      const startY = event.clientY;
      setDragging(true);

      const onMove = (moveEvent: PointerEvent) => {
        setOffset({
          x: moveEvent.clientX - startX,
          y: moveEvent.clientY - startY,
        });
      };
      const onUp = (upEvent: PointerEvent) => {
        cleanupRef.current?.();
        setDragging(false);
        setOffset(ORIGIN);
        onSnap(cornerFor(upEvent.clientX, upEvent.clientY));
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      cleanupRef.current = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        cleanupRef.current = null;
      };
    },
    [enabled, onSnap],
  );

  return { offset, dragging, onPointerDown };
}
